// Centralized date utilities for mock date formatting/parsing and row date extraction

// Standard month abbreviations; display functions will render September as 'Sept' per project preference.
const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTH_MAP = MONTHS_SHORT.reduce((a,m,i)=>{a[m]=i;return a;},{});

// Return display label for month index (0-11); render 'Sept' for September
function monthLabel(idx){
  if(idx === 8) return 'Sept';
  return MONTHS_SHORT[idx] || '';
}

export function formatToMockDate(date){
  if(!(date instanceof Date) || isNaN(date)) return null;
  return `${monthLabel(date.getMonth())} ${date.getDate()}, ${date.getFullYear()}`;
}

export function parseMockDateToIso(mock){
  if(!mock) return null;
  if(/^\d{4}-\d{2}-\d{2}$/.test(mock)) return mock;
  // Accept both 'Sep' and 'Sept' for compatibility
  const m = mock.trim().match(/^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)\s+(\d{1,2}),\s+(\d{4})$/);
  if(!m) return null;
  const [,monRaw,dStr,yStr]=m;
  const mon = monRaw === 'Sept' ? 'Sep' : monRaw; // normalize 'Sept' -> 'Sep' for mapping
  const mi = MONTH_MAP[mon];
  if(mi==null) return null;
  const d = new Date(Number(yStr), mi, Number(dStr));
  if(isNaN(d)) return null;
  const yyyy=d.getFullYear();
  const mm=String(d.getMonth()+1).padStart(2,'0');
  const dd=String(d.getDate()).padStart(2,'0');
  return `${yyyy}-${mm}-${dd}`;
}

export function extractRowDateIso(row){
  if(!row || typeof row!=='object') return null;
  const fields=['__date','date','dateString','timestamp','time','createdAt','ts'];
  for(const f of fields){
    const raw=row[f];
    if(!raw) continue;
    if(typeof raw==='string'){
      if(/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0,10);
  const mockHead=raw.match(/^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)\s+\d{1,2},\s+\d{4}/);
      if(mockHead){
        const iso=parseMockDateToIso(mockHead[0]);
        if(iso) return iso;
      }
    }
    const d=new Date(raw);
    if(!isNaN(d)){
      const yyyy=d.getFullYear();
      const mm=String(d.getMonth()+1).padStart(2,'0');
      const dd=String(d.getDate()).padStart(2,'0');
      return `${yyyy}-${mm}-${dd}`;
    }
  }
  return null;
}

// Format a Date to 'Mon D, YYYY' (September as 'Sept')
export function formatDisplayDate(date){
  if(!(date instanceof Date) || isNaN(date)) return '';
  return `${monthLabel(date.getMonth())} ${date.getDate()}, ${date.getFullYear()}`;
}

// Format a date string or Date object to 'Mon D, YYYY' (September as 'Sept')
export function formatDisplayDateFromString(dateInput){
  if(!dateInput) return '';
  
  let date;
  if(dateInput instanceof Date) {
    date = dateInput;
  } else if(typeof dateInput === 'string') {
    date = new Date(dateInput);
  } else {
    return '';
  }
  
  if(isNaN(date)) return '';
  
  return `${monthLabel(date.getMonth())} ${date.getDate()}, ${date.getFullYear()}`;
}

// Format a Date to 'Mon D, YYYY HH:mm:ss.SSS' (24h), September as 'Sept'
export function formatDisplayDateTime(date){
  if(!(date instanceof Date) || isNaN(date)) return '';
  const mon = monthLabel(date.getMonth());
  const d = date.getDate();
  const y = date.getFullYear();
  const hh = String(date.getHours()).padStart(2,'0');
  const mm = String(date.getMinutes()).padStart(2,'0');
  const ss = String(date.getSeconds()).padStart(2,'0');
  const ms = String(date.getMilliseconds()).padStart(3,'0');
  return `${mon} ${d}, ${y} ${hh}:${mm}:${ss}.${ms}`;
}

// Format a date string or Date object to 'Mon D, YYYY HH:mm:ss.SSS' (24h)
export function formatDisplayDateTimeFromString(dateInput){
  if(!dateInput) return '';
  
  let date;
  if(dateInput instanceof Date) {
    date = dateInput;
  } else if(typeof dateInput === 'string') {
    date = new Date(dateInput);
  } else {
    return '';
  }
  
  if(isNaN(date)) return '';
  
  const mon = monthLabel(date.getMonth());
  const d = date.getDate();
  const y = date.getFullYear();
  const hh = String(date.getHours()).padStart(2,'0');
  const mm = String(date.getMinutes()).padStart(2,'0');
  const ss = String(date.getSeconds()).padStart(2,'0');
  const ms = String(date.getMilliseconds()).padStart(3,'0');
  return `${mon} ${d}, ${y} ${hh}:${mm}:${ss}.${ms}`;
}

// Format a date string or Date object to 'Mon D, YYYY HH:mm' (24h, no seconds)
export function formatDisplayDateTimeNoSecondsFromString(dateInput){
  if(!dateInput) return '';
  
  let date;
  if(dateInput instanceof Date) {
    date = dateInput;
  } else if(typeof dateInput === 'string') {
    date = new Date(dateInput);
  } else {
    return '';
  }
  
  if(isNaN(date)) return '';
  
  const mon = monthLabel(date.getMonth());
  const d = date.getDate();
  const y = date.getFullYear();
  const hh = String(date.getHours()).padStart(2,'0');
  const mm = String(date.getMinutes()).padStart(2,'0');
  return `${mon} ${d}, ${y} ${hh}:${mm}`;
}

// Compact date range label from mock or ISO strings
// Examples:
// - same day: "Sept 24, 2025"
// - same month/year: "Sept 24–25, 2025"
// - same year, different months: "Sept 30 – Oct 2, 2025"
// - different years: "Dec 31, 2025 – Jan 1, 2026"
export function formatCompactDateRange(from, to){
  if(!from || !to) return 'Select date';
  if(from === to) return from;
  const fIso = parseMockDateToIso(from) || (String(from).match(/^\d{4}-\d{2}-\d{2}/) ? String(from).slice(0,10) : null);
  const tIso = parseMockDateToIso(to) || (String(to).match(/^\d{4}-\d{2}-\d{2}/) ? String(to).slice(0,10) : null);
  if(!fIso || !tIso) return `${from} – ${to}`;
  const f = new Date(`${fIso}T00:00:00`);
  const t = new Date(`${tIso}T00:00:00`);
  const fM = monthLabel(f.getMonth());
  const tM = monthLabel(t.getMonth());
  const fD = f.getDate();
  const tD = t.getDate();
  const fY = f.getFullYear();
  const tY = t.getFullYear();
  if (fY === tY && f.getMonth() === t.getMonth()) {
    return `${fM} ${fD}–${tD}, ${fY}`;
  }
  if (fY === tY) {
    return `${fM} ${fD} – ${tM} ${tD}, ${fY}`;
  }
  return `${fM} ${fD}, ${fY} – ${tM} ${tD}, ${tY}`;
}

export { MONTHS_SHORT };

// Convenience helpers to standardize how 'today' is used across the app
export function getTodayIsoDate(){
  const d = new Date();
  const yyyy=d.getFullYear();
  const mm=String(d.getMonth()+1).padStart(2,'0');
  const dd=String(d.getDate()).padStart(2,'0');
  return `${yyyy}-${mm}-${dd}`;
}

export function getTodayMockDate(){
  return formatDisplayDate(new Date());
}

// --- ISO helpers for range comparisons ---
export function orderIsoRange(a, b) {
  if (!a || !b) return [null, null];
  return a <= b ? [a, b] : [b, a];
}

export function isWithinIsoRange(x, start, end) {
  if (!x || !start || !end) return false;
  return x >= start && x <= end;
}
